/* eslint-disable react/forbid-prop-types */
import Head from 'next/head';
import PropTypes from 'prop-types';
import SEO from '@seo/index';
import { fetchTopologyInfo } from '@actions/topologies';
import {
  fetchTopologyEnrolledCourses,
  fetchMoreCoursesFilters,
  fetchMoreCourses
} from '@actions/courses';
import { useSetSelectedNav } from '@stores/sidenav';
import subTopicSchema from '@seo/topic/topic';
import {
  getIsSyllabusV1Enabled,
  getIsTopologySelfDataFetched
} from '@selectors/topologies';
import { getIsLoggedIn, getMe } from '@selectors/auth';
import { getNoFiltersKey } from '@cont/PlusCourses/syllabusHelper';
import TopicPage from '@cont/Syllabus/TopicPage/TopicPage';
import { redirectToRoute } from '@utils/route';
import {
  apiFetcher,
  getTopicInfoEndpoint
} from '@cont/Syllabus/constants/apiHelper';
import { SWRConfig } from 'swr';
import setupInitialProps from '@utils/setupInitialProps';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import Layout from '@layout/index';
import fetchIsGoalGTP from '@api/fetchers/constants';
import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { hasActiveSubscriptionToGoal } from '@models/Me';

const PlusConceptPage = ({ goalInfo, topicInfo, topicUID, fallback }) => {
  const {
    name: goalName,
    goalSlug,
    uid: goalUID,
    thumbnail: goalThumbnail
  } = goalInfo || {};
  const {
    name: topicName,
    slug: topicSlug,
    thumbnail: topicThumbnail,
    parent = {}
  } = topicInfo || {};

  const metaTitle = `${goalName} ${topicName} - Prepare with Unacademy`;
  const metaDescription = `Enroll for ${goalName} ${topicName} Course on Unacademy and learn
  from Top Educators. Subscribe today and get access to complete syllabus and course.`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${goalUID}/topic/${topicSlug}/${topicUID}`;

  useSetSelectedNav('topics');
  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: topicThumbnail || goalThumbnail
            }
          ],
          url: canonicalURL
        }}
        schema={subTopicSchema({
          goal: goalInfo,
          topic: {
            name: parent.name,
            permalink: `https://unacademy.com/goal/${goalSlug}/${goalUID}/topic/${topicSlug}/`
          },
          others: [{ name: topicName, permalink: canonicalURL }],
          type: 'courses',
          topicInfo,
          description: metaDescription
        })}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <SWRConfig value={{ fallback }}>
        <TopicPage goalUID={goalUID} goalSlug={goalSlug} topicUID={topicUID} />
      </SWRConfig>
    </>
  );
};

PlusConceptPage.pageInfo = {
  isMWebSupported: true,
  name: 'Syllabus Topic Chapters Page',
  layoutOptions: {
    showPlusNav: false
  }
};

export default PlusConceptPage;

PlusConceptPage.getInitialProps = setupInitialProps(
  async ({ store, query, isServer, res }) => {
    const {
      goalUID,
      goalSlug,
      topicUid: topicGroupUID,
      conceptUid: topicUID,
      educatorUID,
      educatorUsername
    } = query;
    let state = store.getState();
    const { dispatch, getState } = store;
    const isLoggedIn = getIsLoggedIn(state);
    const isSubscribed = getIsSubscribedToPlus(goalUID)(state);
    const isOfflineSubscribed = getIsSubscribedToOffline(goalUID)(state);

    const meData = getMe(state);
    const isPlusUser = hasActiveSubscriptionToGoal(meData, goalUID);
    const isGTP = await fetchIsGoalGTP(goalUID, store);

    // Educator Library is Enabled
    if (
      educatorUID &&
      (!isLoggedIn || !(isSubscribed || isOfflineSubscribed))
    ) {
      if (educatorUsername) {
        redirectToRoute(isServer, res, `/@${educatorUsername}`);
      } else {
        redirectToRoute(
          isServer,
          res,
          `/goal/${goalSlug}/${goalUID}/topic/${topicGroupUID}/${topicUID}`
        );
      }
    }
    if (!isPlusUser && isGTP)
      return redirectToRoute(isServer, res, `/goal/${goalSlug}/${goalUID}`);

    if (goalUID && topicUID) {
      const promises = [];
      if (!getIsTopologySelfDataFetched(state, goalUID))
        promises.push(store.dispatch(fetchTopologyInfo(goalUID)));
      if (!getIsTopologySelfDataFetched(state, topicUID))
        promises.push(store.dispatch(fetchTopologyInfo(topicUID)));
      if (isLoggedIn && (isSubscribed || isOfflineSubscribed)) {
        promises.push(
          store.dispatch(
            fetchTopologyEnrolledCourses(topicUID, false, false, '', 5)
          )
        );
      }
      promises.push(store.dispatch(fetchMoreCoursesFilters(goalUID)));
      promises.push(
        store.dispatch(
          fetchMoreCourses({
            topicUid: topicUID,
            key: getNoFiltersKey(topicUID)
          })
        )
      );

      await Promise.all(promises);
    }

    state = store.getState();
    const isNewSyllabusEnabled = getIsSyllabusV1Enabled(state, goalUID);
    const topicInfoEndpoint = getTopicInfoEndpoint(
      goalUID,
      topicUID,
      educatorUID
    );

    if (!isNewSyllabusEnabled) {
      redirectToRoute(
        isServer,
        res,
        `/goal/${goalSlug}/${goalUID}/topic/${topicUID}/courses`
      );
    }

    let fallback = {};
    if (goalUID && topicUID && isNewSyllabusEnabled) {
      const syllabusTopicpageInfo = await apiFetcher(topicInfoEndpoint, {
        dispatch,
        getState
      });
      fallback = { [topicInfoEndpoint]: syllabusTopicpageInfo };
    }
    state = store.getState();
    return {
      fallback,
      topicUID: escape(topicUID),
      goalInfo: state.topologies.data[goalUID],
      topicInfo: state.topologies.data[topicUID]
    };
  },
  PlusConceptPage.pageInfo
);

PlusConceptPage.propTypes = {
  goalInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  topicInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  topicUID: PropTypes.string.isRequired,
  fallback: PropTypes.shape({ [PropTypes.string]: PropTypes.any }).isRequired
};
PlusConceptPage.Layout = Layout;
PlusConceptPage.Wrapper = PageReduxProvider;
